import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getAppForHost, type AppKey } from "@/lib/hostname/appForHost";

// Per-host favicon, <title>, meta description, Open Graph, Twitter Card,
// and canonical URL. SPAs can't serve different static <head> per domain,
// so we mutate it at runtime. Idempotent — runs on every host/route change.

interface BrandMeta {
  title: string;
  description: string;
  origin: string;
  ogImage: string;
  siteName: string;
  twitterHandle?: string;
}

// Per-host favicons + Open Graph images now point at the boss-uploaded
// brand artwork in the public `email-assets` Cloud storage bucket. This
// keeps web, email, and share previews on the same single source of truth.
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const STORAGE_BASE = `${SUPABASE_URL}/storage/v1/object/public/email-assets`;
const brandLogo = (slug: string) => `${STORAGE_BASE}/${slug}_logo`;

const ICONS: Record<AppKey, string> = {
  wavlynq: brandLogo("wavlynq"),
  fryqnc: brandLogo("fryqnc"),
  pulsynq: brandLogo("pulsynq"),
  mindlynq: brandLogo("mindlynq"),
  ablelynq: brandLogo("ablelynq"),
  deafwav: brandLogo("deafwav"),
  fullynq: brandLogo("fullynq"),
  jadesynq: brandLogo("jadesynq"),
  openlynq: brandLogo("openlynq"),
  spiritsynq: brandLogo("spiritsynq"),
};

const BRAND: Record<AppKey, BrandMeta> = {
  wavlynq: {
    siteName: "Wavlynq",
    title: "Wavlynq — One signal. Nine communities.",
    description:
      "Wavlynq is the umbrella for nine culturally-grounded community apps — discovery, mentorship, and a crisis-aware companion, built for the people who use it. 18+.",
    origin: "https://www.wavlynq.com",
    ogImage: "https://www.wavlynq.com/og-image.png",
  },
  fryqnc: {
    siteName: "Fryqncy",
    title: "Fryqncy — LGBTQIA+ community, mentorship & connection.",
    description:
      "Fryqncy is the LGBTQIA+ home in the Wavlynq network — discovery, verified mentors, and a crisis-aware companion. 18+.",
    origin: "https://www.fryqnc.com",
    ogImage: "https://www.fryqnc.com/og-image.png",
  },
  pulsynq: {
    siteName: "Pulsynq",
    title: "Pulsynq — Black community, mentorship & connection.",
    description:
      "Pulsynq is the Black community home in the Wavlynq network — discovery, verified mentors, and a crisis-aware companion. 18+.",
    origin: "https://www.pulsynq.com",
    ogImage: "https://www.pulsynq.com/og-image.png",
  },
  mindlynq: {
    siteName: "MindLynq",
    title: "MindLynq — Neurodivergent community, mentorship & connection.",
    description:
      "MindLynq is the neurodivergent home in the Wavlynq network — discovery, verified mentors, and a crisis-aware companion. 18+.",
    origin: "https://www.mindlynq.com",
    ogImage: "https://www.mindlynq.com/og-image.png",
  },
  ablelynq: {
    siteName: "Ablelynq",
    title: "Ablelynq — Disability community, mentorship & connection.",
    description:
      "Ablelynq is the disability community home in the Wavlynq network — discovery, verified mentors, and a crisis-aware companion. 18+.",
    origin: "https://www.ablelynq.com",
    ogImage: "https://www.ablelynq.com/og-image.png",
  },
  deafwav: {
    siteName: "Deafwav",
    title: "Deafwav — Deaf & HoH community, mentorship & connection.",
    description:
      "Deafwav is the Deaf and Hard-of-Hearing home in the Wavlynq network — discovery, verified mentors, and a crisis-aware companion. 18+.",
    origin: "https://www.deafwav.com",
    ogImage: "https://www.deafwav.com/og-image.png",
  },
  fullynq: {
    siteName: "Fullynq",
    title: "Fullynq — Body-positive community, mentorship & connection.",
    description:
      "Fullynq is the body-positive home in the Wavlynq network — discovery, verified mentors, and a crisis-aware companion. 18+.",
    origin: "https://www.fullynq.com",
    ogImage: "https://www.fullynq.com/og-image.png",
  },
  jadesynq: {
    siteName: "Jadesynq",
    title: "Jadesynq — AAPI community, mentorship & connection.",
    description:
      "Jadesynq is the AAPI home in the Wavlynq network — discovery, verified mentors, and a crisis-aware companion. 18+.",
    origin: "https://www.jadesynq.com",
    ogImage: "https://www.jadesynq.com/og-image.png",
  },
  openlynq: {
    siteName: "Openlynq",
    title: "Openlynq — Polyamorous & ENM community, mentorship & connection.",
    description:
      "Openlynq is the polyamorous and ethically non-monogamous home in the Wavlynq network — discovery, verified mentors, and a crisis-aware companion. 18+.",
    origin: "https://www.openlynq.com",
    ogImage: "https://www.openlynq.com/og-image.png",
  },
  spiritsynq: {
    siteName: "Spiritsynq",
    title: "Spiritsynq — Faith & spirituality community, mentorship & connection.",
    description:
      "Spiritsynq is the faith and spirituality home in the Wavlynq network — discovery, verified mentors, and a crisis-aware companion. 18+.",
    origin: "https://www.spiritsynq.com",
    ogImage: "https://www.spiritsynq.com/og-image.png",
  },
};

/** Upsert a `<meta name="...">` or `<meta property="...">` tag. */
const setMeta = (key: "name" | "property", value: string, content: string) => {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[${key}="${value}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(key, value);
    document.head.appendChild(tag);
  }
  if (tag.content !== content) tag.content = content;
};

const setLink = (rel: string, href: string, type?: string) => {
  // Remove any duplicates of the same rel before inserting (browsers can be
  // picky about which `<link rel="icon">` they actually use).
  const existing = document.head.querySelectorAll<HTMLLinkElement>(`link[rel="${rel}"]`);
  existing.forEach((el) => el.parentNode?.removeChild(el));
  const link = document.createElement("link");
  link.rel = rel;
  link.href = href;
  if (type) link.type = type;
  document.head.appendChild(link);
};

const BrandHead = () => {
  const { pathname, search } = useLocation();

  useEffect(() => {
    const app = getAppForHost();
    const meta = BRAND[app];
    const iconHref = ICONS[app];
    const iconType = iconHref.endsWith(".svg") ? "image/svg+xml" : "image/png";
    const url = `${meta.origin}${pathname}${search}`;

    // Title
    if (document.title !== meta.title) document.title = meta.title;

    // Description + canonical
    setMeta("name", "description", meta.description);
    setLink("canonical", url);

    // Open Graph
    setMeta("property", "og:site_name", meta.siteName);
    setMeta("property", "og:title", meta.title);
    setMeta("property", "og:description", meta.description);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", meta.ogImage);

    // Twitter Card
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", meta.title);
    setMeta("name", "twitter:description", meta.description);
    setMeta("name", "twitter:image", meta.ogImage);

    // Favicon (icon + apple-touch-icon)
    setLink("icon", iconHref, iconType);
    setLink("shortcut icon", iconHref, iconType);
    setLink("apple-touch-icon", iconHref);
  }, [pathname, search]);

  return null;
};

export default BrandHead;
